import { useEffect, useState } from "react";
import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/i18n/LanguageContext";
import { OPEN_COOKIE_SETTINGS_EVENT, clearAnalyticsCookies, readConsent, saveConsent } from "@/lib/consent";

const COPY = {
  es: {
    title: "Usamos cookies",
    body: "Usamos cookies técnicas necesarias y, solo si nos dejas, cookies de analítica (Google Analytics) y de marketing. Puedes aceptarlas, rechazarlas o elegir cuáles.",
    accept: "Aceptar todas",
    reject: "Rechazar todas",
    configure: "Configurar",
    save: "Guardar selección",
    cookies: "Política de cookies",
    privacy: "Política de privacidad",
    necessary: "Necesarias",
    necessaryDesc: "Imprescindibles para que la web funcione (sesión, seguridad, idioma). Siempre activas.",
    always: "Siempre activas",
    analytics: "Analítica",
    analyticsDesc: "Google Analytics: nos ayuda a entender cómo se usa la web para mejorarla.",
    marketing: "Marketing",
    marketingDesc: "Publicidad y medición de campañas.",
    cookiesPath: "/cookies",
    privacyPath: "/privacidad",
  },
  en: {
    title: "We use cookies",
    body: "We use necessary technical cookies and, only with your permission, analytics (Google Analytics) and marketing cookies. You can accept, reject or choose which ones.",
    accept: "Accept all",
    reject: "Reject all",
    configure: "Configure",
    save: "Save selection",
    cookies: "Cookie policy",
    privacy: "Privacy policy",
    necessary: "Necessary",
    necessaryDesc: "Required for the website to work (session, security, language). Always active.",
    always: "Always active",
    analytics: "Analytics",
    analyticsDesc: "Google Analytics: helps us understand how the website is used so we can improve it.",
    marketing: "Marketing",
    marketingDesc: "Advertising and campaign measurement.",
    cookiesPath: "/en/cookies",
    privacyPath: "/en/privacy-policy",
  },
};

const CookieConsent = () => {
  const { lang } = useLanguage();
  const c = COPY[lang === "en" ? "en" : "es"];
  const [open, setOpen] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const current = readConsent();
    if (!current?.analytics) clearAnalyticsCookies();
    if (!current) setOpen(true);
    const onOpen = () => {
      const saved = readConsent();
      setAnalytics(saved?.analytics ?? false);
      setMarketing(saved?.marketing ?? false);
      setConfiguring(true);
      setOpen(true);
    };
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, onOpen);
  }, []);

  if (!open) return null;

  const finish = (a: boolean, m: boolean) => {
    saveConsent(a, m);
    setOpen(false);
    setConfiguring(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="rl-cookie-title"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-card shadow-lg"
    >
      <div className="container max-w-5xl py-4 font-body">
        <h2 id="rl-cookie-title" className="font-display text-base font-bold">{c.title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {c.body}{" "}
          <Link to={c.cookiesPath} className="underline hover:text-foreground">{c.cookies}</Link>
          {" · "}
          <Link to={c.privacyPath} className="underline hover:text-foreground">{c.privacy}</Link>
        </p>

        {configuring && (
          <div className="mt-4 space-y-3">
            <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-semibold">{c.necessary}</p>
                <p className="text-xs text-muted-foreground">{c.necessaryDesc}</p>
              </div>
              <span className="shrink-0 text-xs font-semibold text-muted-foreground">{c.always}</span>
            </div>
            <label className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-semibold">{c.analytics}</p>
                <p className="text-xs text-muted-foreground">{c.analyticsDesc}</p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label={c.analytics} />
            </label>
            <label className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
              <div>
                <p className="text-sm font-semibold">{c.marketing}</p>
                <p className="text-xs text-muted-foreground">{c.marketingDesc}</p>
              </div>
              <Switch checked={marketing} onCheckedChange={setMarketing} aria-label={c.marketing} />
            </label>
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Button className="min-h-11 w-full" onClick={() => finish(true, true)}>{c.accept}</Button>
          <Button className="min-h-11 w-full" onClick={() => finish(false, false)}>{c.reject}</Button>
          {configuring ? (
            <Button className="min-h-11 w-full" onClick={() => finish(analytics, marketing)}>{c.save}</Button>
          ) : (
            <Button className="min-h-11 w-full" onClick={() => setConfiguring(true)}>{c.configure}</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;

/** Footer link that reopens the consent panel. */
export const CookieSettingsLink = ({ className }: { className?: string }) => {
  const { lang } = useLanguage();
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT))}
      className={className ?? "hover:text-foreground transition-colors"}
    >
      {lang === "en" ? "Cookie settings" : "Configurar cookies"}
    </button>
  );
};
